/**
 * Teams, persisted and reactive. The `workspace.ts` of the multiplayer half.
 *
 * ── Why this opens its own database ────────────────────────────────────────────────────────
 *
 * `storage.ts` owns `euclid` at `DB_VERSION = 2`, and several features are being added to it at
 * once. An IndexedDB version number is a single global lock: two features that each bump it to 3
 * produce a database where whichever ran second throws `VersionError` and the app that was
 * working yesterday cannot open its own storage. That failure is invisible until it happens on
 * someone's machine, and it is not recoverable without deleting their work.
 *
 * So teams live in `euclid-teams`, opened by this file and no other. Nothing here touches
 * `storage.ts`, needs its version, or cares when it changes.
 *
 * The open is defensive anyway, because being right for the wrong reason is not being right:
 * it opens with NO explicit version, looks at what stores exist, and only then reopens at
 * `version + 1` to create what is missing. That is the pattern that survives someone else having
 * already bumped the database, and it is also the pattern the testing gotcha in CLAUDE.md asks
 * for — `onerror` and `onblocked` are wired on every request, so a blocked upgrade rejects
 * instead of hanging a promise that never settles.
 */

import { computed, ref } from 'vue';
import { activeAccount, scopedName } from './workspaces';

import { uuidv7 } from './merge';
import type { InkName } from './scene';
import {
  MEMBER_INKS, addMember, createTeam, findByCode, fromStoredTeam, isJoinCode, newMember,
  nextMemberInk, normaliseCode, removeMember, renameTeam, rotateCode, shareProject, toStoredTeam,
  unshareProject, type Team, type TeamMember,
} from './teams';

/*
 * The base name. What is opened is this scoped to the signed-in account — the teams a person has
 * joined belong to that person, and finding somebody else's teams after signing in as yourself is
 * the same fault as finding their sheets.
 */
const DB_NAME = 'euclid-teams';
const TEAMS = 'teams';
const IDENTITY = 'identity';
const SHEETS = 'sheets';
const WANTED = [TEAMS, IDENTITY, SHEETS];

/**
 * The local person.
 *
 * PLAN.md Part II §10.5 makes identity local-first and says plainly that when teams arrive this
 * record is ADOPTED rather than rebuilt. So this is the minimum a team needs — an id, a name, a
 * colour — and nothing else. When the profile surface lands, `setIdentity` is the one call that
 * feeds it in; nothing else in this file needs to know.
 */
export interface Identity {
  id: string;
  name: string;
  ink: InkName;
}

const request = <T,>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const openAt = (version?: number): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const name = scopedName(DB_NAME, activeAccount());
    const req = version === undefined ? indexedDB.open(name) : indexedDB.open(name, version);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const name of WANTED) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    // A second tab holding the old version blocks the upgrade forever. Rejecting is the only
    // honest answer; a promise that never settles is the bug CLAUDE.md §13 was written about.
    req.onblocked = () => reject(new Error('another tab is holding euclid-teams open'));
  });

let handle: Promise<IDBDatabase> | null = null;

export function openTeamDb(): Promise<IDBDatabase> {
  if (handle) return handle;
  handle = (async () => {
    let db = await openAt();
    if (WANTED.every(name => db.objectStoreNames.contains(name))) return db;
    const next = db.version + 1;
    db.close();
    db = await openAt(next);
    return db;
  })();
  return handle;
}

const run = async <T,>(
  store: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openTeamDb();
  const tx = db.transaction(store, mode);
  return request(work(tx.objectStore(store)));
};

// ── State ──────────────────────────────────────────────────────────────────────────────────

const teams = ref<Team[]>([]);
const identity = ref<Identity | null>(null);
const activeTeamId = ref<string | null>(null);
const ready = ref(false);

const ACTIVE_KEY = 'euclid.team.active';

/** A name that reads as a person rather than as an id, for someone who has not set one yet. */
const DEFAULT_NAMES = ['Owl', 'Heron', 'Marten', 'Lynx', 'Falcon', 'Otter'];

export function defaultIdentity(at = Date.now()): Identity {
  return {
    id: uuidv7(at),
    name: DEFAULT_NAMES[at % DEFAULT_NAMES.length]!,
    ink: MEMBER_INKS[0]!,
  };
}

export async function loadTeams(): Promise<void> {
  if (ready.value) return;
  try {
    const [storedTeams, storedIdentity] = await Promise.all([
      run<unknown[]>(TEAMS, 'readonly', store => store.getAll()),
      run<(Identity & { memberId?: string }) | undefined>(IDENTITY, 'readonly', store => store.get('me')),
    ]);
    teams.value = storedTeams.map(fromStoredTeam).filter((team): team is Team => team !== null);
    identity.value = readIdentityRow(storedIdentity);
  } catch {
    // Private browsing, a locked-down profile, a blocked upgrade. Teams are an addition; the app
    // opens without them rather than failing to open.
    teams.value = [];
    identity.value = null;
  }
  if (!identity.value) {
    identity.value = defaultIdentity();
    await persistIdentity();
  }
  try { activeTeamId.value = globalThis.localStorage?.getItem(ACTIVE_KEY) ?? null; } catch { /* no storage */ }
  ready.value = true;
}

// The identity row is keyed 'me' so there is only ever one; the member id rides alongside,
// because overwriting it with the key would make every reload a different person.
const readIdentityRow = (row: (Identity & { memberId?: string }) | undefined): Identity | null =>
  row ? { id: row.memberId ?? row.id, name: row.name, ink: row.ink } : null;

const persistTeam = async (team: Team): Promise<void> => {
  try { await run(TEAMS, 'readwrite', store => store.put(toStoredTeam(team)) as IDBRequest<IDBValidKey>); }
  catch { /* the in-memory team still works for this session */ }
};

const persistIdentity = async (): Promise<void> => {
  const me = identity.value;
  if (!me) return;
  try { await run(IDENTITY, 'readwrite', store => store.put({ ...me, id: 'me', memberId: me.id }) as IDBRequest<IDBValidKey>); }
  catch { /* as above */ }
};

// ── Reads ──────────────────────────────────────────────────────────────────────────────────

export const useTeams = () => ({
  ready,
  teams: computed(() => [...teams.value].sort((a, b) => b.updatedAt - a.updatedAt)),
  identity: computed(() => identity.value),
  activeTeam: computed(() => teams.value.find(team => team.id === activeTeamId.value) ?? null),
  activeTeamId: computed(() => activeTeamId.value),
});

export const teamById = (id: string): Team | undefined => teams.value.find(team => team.id === id);

/** Every team that has this project on its shared list — usually one, occasionally none. */
export const teamsWithProject = (projectId: string): Team[] =>
  teams.value.filter(team => team.projectIds.includes(projectId));

// ── Writes ─────────────────────────────────────────────────────────────────────────────────

export async function setIdentity(changes: Partial<Identity>): Promise<Identity> {
  const me = { ...(identity.value ?? defaultIdentity()), ...changes };
  identity.value = me;
  await persistIdentity();
  // The name and colour a person shows up as are stored on every team they are in, so a rename
  // has to reach all of them or the roster keeps calling them what they used to be called.
  for (const team of teams.value) {
    if (team.members.some(member => member.id === me.id)) {
      await write(team.id, current => ({
        ...current,
        members: current.members.map(m => (m.id === me.id ? { ...m, name: me.name, ink: me.ink } : m)),
      }));
    }
  }
  return me;
}

const write = async (teamId: string, change: (team: Team) => Team): Promise<Team | null> => {
  const current = teamById(teamId);
  if (!current) return null;
  const next = change(current);
  teams.value = teams.value.map(team => (team.id === teamId ? next : team));
  await persistTeam(next);
  return next;
};

export async function makeTeam(name: string): Promise<Team> {
  const me = identity.value ?? defaultIdentity();
  identity.value = me;
  const team = createTeam(name, { id: me.id, name: me.name, ink: me.ink });
  teams.value = [...teams.value, team];
  await persistTeam(team);
  await setActiveTeam(team.id);
  return team;
}

export type JoinOutcome =
  | { ok: true; team: Team; alreadyIn: boolean }
  | { ok: false; reason: 'malformed' | 'unknown' };

/**
 * Join by code.
 *
 * Against a server this is one request. Locally it can only find a team this browser already has
 * — which is exactly the case that matters for two tabs, and is honest about what it cannot do:
 * a code from someone else's machine has nowhere to resolve until there is a server. The failure
 * is `unknown` rather than a lie.
 */
export async function joinByCode(code: string): Promise<JoinOutcome> {
  if (!isJoinCode(code)) return { ok: false, reason: 'malformed' };
  const found = findByCode(teams.value, normaliseCode(code));
  if (!found) return { ok: false, reason: 'unknown' };

  const me = identity.value ?? defaultIdentity();
  identity.value = me;
  const alreadyIn = found.members.some(member => member.id === me.id);
  const next = addMember(
    found,
    newMember({ id: me.id, name: me.name, ink: nextMemberInk(found) }, found),
  );
  await write(found.id, () => next);
  await setActiveTeam(next.id);
  return { ok: true, team: next, alreadyIn };
}

export async function leaveTeam(teamId: string): Promise<void> {
  const me = identity.value;
  const team = teamById(teamId);
  if (!me || !team) return;
  const next = removeMember(team, me.id);
  if (next.members.length === 0) {
    teams.value = teams.value.filter(t => t.id !== teamId);
    try { await run(TEAMS, 'readwrite', store => store.delete(teamId) as unknown as IDBRequest<undefined>); }
    catch { /* gone from memory either way */ }
  } else {
    await write(teamId, () => next);
  }
  if (activeTeamId.value === teamId) await setActiveTeam(null);
}

export const rename = (teamId: string, name: string): Promise<Team | null> =>
  write(teamId, team => renameTeam(team, name));

export const newCode = (teamId: string): Promise<Team | null> =>
  write(teamId, team => rotateCode(team));

export const share = (teamId: string, projectId: string): Promise<Team | null> =>
  write(teamId, team => shareProject(team, projectId));

export const unshare = (teamId: string, projectId: string): Promise<Team | null> =>
  write(teamId, team => unshareProject(team, projectId));

export const setMemberInk = (teamId: string, memberId: string, ink: InkName): Promise<Team | null> =>
  write(teamId, team => ({
    ...team,
    members: team.members.map((m): TeamMember => (m.id === memberId ? { ...m, ink } : m)),
  }));

export async function setActiveTeam(teamId: string | null): Promise<void> {
  activeTeamId.value = teamId;
  try {
    if (teamId) globalThis.localStorage?.setItem(ACTIVE_KEY, teamId);
    else globalThis.localStorage?.removeItem(ACTIVE_KEY);
  } catch { /* no storage */ }
}

/** Test seam: drop the in-memory state so a fresh `loadTeams` re-reads the database. */
export function resetTeamState(): void {
  teams.value = [];
  identity.value = null;
  activeTeamId.value = null;
  ready.value = false;
  handle = null;
}

/**
 * Close the current database and forget everything read out of it.
 *
 * Called when the signed-in account changes. Closing matters: an open connection blocks a version
 * change on that name and, on WebKit, blocks writes outright — the failure that made the built-in
 * chapters silently refuse to install.
 */
export async function reopenTeams(): Promise<void> {
  const previous = handle;
  resetTeamState();
  try { (await previous)?.close(); } catch { /* a failed open has nothing to close */ }
}

// ── Sheets, on this device ─────────────────────────────────────────────────────────────────

/**
 * A shared sheet, in the shape both backends agree on.
 *
 * Against Supabase this is a row in `public.sheets` under the team's project. Here it is a row
 * in the same `euclid-teams` database the teams live in, so a team and its sheets survive a
 * reload together or not at all — a team whose sheets vanished would be worse than no team.
 */
export interface TeamSheet {
  id: string;
  teamId: string;
  name: string;
  createdAt: number;
}

export async function localSheets(teamId: string): Promise<TeamSheet[]> {
  try {
    const all = await run<TeamSheet[]>(SHEETS, 'readonly', store => store.getAll());
    return all.filter(sheet => sheet.teamId === teamId).sort((a, b) => a.createdAt - b.createdAt);
  } catch { return []; }
}

export async function addLocalSheet(teamId: string, name: string): Promise<TeamSheet> {
  const at = Date.now();
  const sheet: TeamSheet = { id: uuidv7(at), teamId, name: name.trim() || 'Sheet', createdAt: at };
  try { await run(SHEETS, 'readwrite', store => store.put(sheet) as IDBRequest<IDBValidKey>); }
  catch { /* in memory for this session is still a usable sheet */ }
  return sheet;
}
